/**
 * RsvpForm — shared RSVP form for templates that do not have their own
 * custom form design.
 *
 * Shows:
 *   - External link button when `externalLink` is provided (host configured an
 *     external RSVP form such as Google Forms).
 *   - Inline name / status / headcount / message form otherwise, wired to the
 *     InviteCloud native RSVP endpoint via `useRsvp`.
 *
 * Visual style: dark-glass, works on any coloured background.
 */

import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useRsvp } from './useRsvp';
import type { RsvpStatus } from './types';

interface RsvpFormProps {
  invitationId?: string | null;
  isLive?: boolean;
  externalLink?: string | null;
  /** Deadline copy shown above the form e.g. "June 30, 2025". */
  deadline?: string;
}

const STATUS_OPTIONS: { value: RsvpStatus; label: string }[] = [
  { value: 'ACCEPTED', label: "Joyfully Accepts ✓" },
  { value: 'MAYBE', label: "Will Try to Attend ~" },
  { value: 'DECLINED', label: "Regretfully Declines ✕" },
];

const fieldCls =
  'w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-white placeholder:text-white/40 outline-none focus:border-white/50 focus:ring-2 focus:ring-white/20 transition-all text-sm';

export function RsvpForm({ invitationId, isLive = false, externalLink, deadline }: RsvpFormProps) {
  const { state, errorMessage, submit, reset } = useRsvp(invitationId, isLive);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<RsvpStatus>('ACCEPTED');
  const [headcount, setHeadcount] = useState(1);
  const [message, setMessage] = useState('');
  const [nameError, setNameError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError('Please enter your name.');
      return;
    }
    setNameError('');
    await submit({
      guestName: name.trim(),
      guestEmail: email.trim() || undefined,
      status,
      headcount: status === 'DECLINED' ? 1 : headcount,
      message: message.trim() || undefined,
    });
  };

  // External link — host configured an external RSVP form
  if (externalLink) {
    return (
      <div className="text-center">
        <a
          href={externalLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-full hover:bg-white/25 transition-all text-sm"
        >
          <ExternalLink size={16} />
          Open RSVP Form
        </a>
      </div>
    );
  }

  // Success state
  if (state === 'success') {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-4">🎉</div>
        <p className="text-white font-semibold text-lg mb-1">Thank you!</p>
        <p className="text-white/60 text-sm">Your RSVP has been recorded.</p>
        <button
          type="button"
          onClick={reset}
          className="mt-4 text-white/50 text-xs underline hover:text-white/80 transition-colors"
        >
          Change my response
        </button>
      </div>
    );
  }

  const loading = state === 'loading';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full max-w-md mx-auto">
      {deadline && (
        <p className="text-center text-white/50 text-xs">
          Kindly respond by <span className="text-white/70">{deadline}</span>
        </p>
      )}

      {/* Status selector */}
      <div className="flex flex-col gap-2">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setStatus(opt.value)}
            className={[
              'w-full py-3 px-4 rounded-xl border text-sm font-medium transition-all',
              status === opt.value
                ? 'border-white/60 bg-white/20 text-white'
                : 'border-white/15 bg-white/5 text-white/60 hover:border-white/30 hover:text-white/80',
            ].join(' ')}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Name */}
      <div>
        <input
          type="text"
          placeholder="Your full name *"
          value={name}
          onChange={(e) => { setName(e.target.value); setNameError(''); }}
          className={fieldCls}
          required
        />
        {nameError && <p className="mt-1 text-red-400 text-xs">{nameError}</p>}
      </div>

      {/* Email (optional) */}
      <input
        type="email"
        placeholder="Email (optional)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={fieldCls}
      />

      {/* Headcount — only when attending */}
      {status !== 'DECLINED' && (
        <div className="flex items-center gap-3">
          <span className="text-white/70 text-sm flex-1">Number attending</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setHeadcount(Math.max(1, headcount - 1))}
              className="w-8 h-8 rounded-full border border-white/20 bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition"
            >
              −
            </button>
            <span className="text-white font-medium w-6 text-center">{headcount}</span>
            <button
              type="button"
              onClick={() => setHeadcount(Math.min(20, headcount + 1))}
              className="w-8 h-8 rounded-full border border-white/20 bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition"
            >
              +
            </button>
          </div>
        </div>
      )}

      {/* Optional note */}
      <textarea
        placeholder="Leave a message (optional)"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={2}
        maxLength={500}
        className={`${fieldCls} resize-none`}
      />

      {/* Error */}
      {state === 'error' && errorMessage && (
        <p className="text-red-400 text-sm text-center">{errorMessage}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full py-4 rounded-full bg-white text-black font-bold text-sm uppercase tracking-widest hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {loading ? 'Sending…' : 'Send RSVP'}
      </button>
    </form>
  );
}
